import { api } from './client'
import type { components } from './generated/schema'
import type { SalesOrderStatus } from './sales-order-api'

// Tài khoản khách bán lẻ (web + Zalo Mini App) — màn Bán lẻ › Tài khoản khách.
// Hợp đồng v3 §14.3, §14.8.

export type ShopAccountStatus = 'active' | 'locked' | 'unverified'
export type ShopProvider = 'email' | 'google' | 'zalo' | 'phone'
export type ShopAccountSource = 'web' | 'zalo_mini_app'

export interface ShopAccountRow {
    id: string
    name: string
    phone: string | null
    phone_verified_at: string | null
    email: string | null
    customer_id: string | null
    customer_code: string | null
    customer_name: string | null
    status: ShopAccountStatus
    source: ShopAccountSource
    spend: number
    last_login_at: string | null
    created_at: string
    providers: ShopProvider[]
    /** Số đơn web / Mini App của tài khoản. */
    orders: number
    /** Hạng theo chi tiêu cache (cập nhật mỗi đêm). */
    tier_name: string | null
}

export interface ShopAccountFilter {
    q?: string
    source?: ShopAccountSource | 'both'
    linked?: boolean
    tier_id?: string
    page?: number
    per_page?: number
}

export interface ShopAccountIdentity {
    provider: ShopProvider
    app_id: string | null
    subject: string
    verified_at: string
    last_login_at: string | null
}

export interface ShopAccountAddress {
    id: string
    recipient_name: string
    phone: string
    province_code: string
    ward_code: string
    address_line: string
    is_default: boolean
}

export interface ShopTierBrief {
    id: string
    name: string
    min_spend: number
    discount_percent: number
    free_shipping: boolean
}

export interface ShopAccountAudit {
    id: string
    actor_name: string | null
    action: 'lock' | 'unlock' | 'revoke_sessions' | 'customer_link' | 'merge'
    created_at: string
}

export interface ShopAccountDetail {
    account: ShopAccountRow
    identities: ShopAccountIdentity[]
    addresses: ShopAccountAddress[]
    tier: {
        spend: number
        current: ShopTierBrief | null
        next: ShopTierBrief | null
        remaining: number | null
    }
    same_phone_accounts: ShopAccountRow[]
    audit: ShopAccountAudit[]
    order_stats: { orders: number; first_order_at: string | null }
}

/** Dòng đơn của tài khoản (dạng tóm tắt của API đơn bán lẻ). */
export interface ShopAccountOrder {
    order_code: string
    status: SalesOrderStatus
    payment_status: string
    payment_method: string | null
    created_at: string
    customer_name: string
    total_amount: number
    item_count: number
}

/** Khối "Tài khoản online" ở chi tiết khách ERP (spec 2026-10-07 §7.2). */
export type CustomerOnlineAccount =
    components['schemas']['CustomerOnlineAccount']

const base = '/shop-admin/accounts'

export const shopAccountApi = {
    list: (f: ShopAccountFilter) =>
        api.get<ShopAccountRow[]>(base, { params: f }),
    get: (id: string) => api.get<ShopAccountDetail>(`${base}/${id}`),
    orders: (id: string, perPage: number) =>
        api.get<ShopAccountOrder[]>(`${base}/${id}/orders`, {
            params: { per_page: perPage },
        }),
    lock: (id: string) => api.post<ShopAccountDetail>(`${base}/${id}/lock`),
    unlock: (id: string) => api.post<ShopAccountDetail>(`${base}/${id}/unlock`),
    revokeSessions: (id: string) =>
        api.post<ShopAccountDetail>(`${base}/${id}/revoke-sessions`),
    /** Tài khoản online của khách ERP; data null khi khách chưa có. */
    customerAccount: (customerId: string) =>
        api.get<CustomerOnlineAccount | null>(
            `/customers/${customerId}/shop-account`,
        ),
    setCustomerLink: (id: string, customerId: string | null) =>
        api.put<ShopAccountDetail>(`${base}/${id}/customer-link`, {
            customer_id: customerId,
        }),
}
